import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "349",
  "slug": "reading-shadows-in-satellite-imagery",
  "title": "How to Read Shadows in Satellite Imagery (And What They Reveal)",
  "excerpt": "Shadows are not noise in a satellite image — they are data. They can tell you the height of a building, the time of day, and even which hemisphere you are looking at.",
  "date": "2026-06-04",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1472146936668-d987bf0a6e38?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxhZXJpYWwlMjBjaXR5JTIwZGF3bnxlbnwxfDB8fHwxNzgwNTYwNDc5fDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "content": [
    {
      "type": "paragraph",
      "text": "When most people look at a satellite image, they read the bright stuff: roads, fields, rooftops, coastline. The shadows get ignored. That is a mistake, because shadows are quietly packed with information — direction, height, time, and terrain all leave their mark in the dark."
    },
    {
      "type": "heading",
      "text": "Shadows Point Away From the Sun"
    },
    {
      "type": "paragraph",
      "text": "Start with the one rule that unlocks the rest: a shadow always falls on the opposite side of an object from the sun. So the moment you find a clear shadow — the dark sliver beside a tall building, the long streak off a lone tree — you know roughly where the sun was sitting when the image was captured. Everything else builds on that single fact."
    },
    {
      "type": "heading",
      "text": "Which Hemisphere Are You In?"
    },
    {
      "type": "paragraph",
      "text": "Here is where it gets useful for guessing. Around midday, the sun sits to the south in the Northern Hemisphere, so shadows fall toward the north. In the Southern Hemisphere it is the reverse — the midday sun is to the north and shadows stretch south. If buildings and trees in an image throw their shadows consistently one way, that is a real hint about which half of the planet you are looking at. The trick fades near the equator, where the sun passes nearly overhead and shadows shrink to almost nothing, but away from the tropics it is a genuine tell."
    },
    {
      "type": "heading",
      "text": "How Tall Is That?"
    },
    {
      "type": "paragraph",
      "text": "Shadow length encodes height. For a given sun angle, a taller object casts a longer shadow, so you can compare the shadows of nearby buildings to rank them by height even when the image is taken straight down and every roof looks flat. Analysts go further and combine the sun's elevation with the measured shadow length to calculate the actual height of a structure — the same trigonometry works on chimneys, towers, and even individual trees. A shadow twice as long as its neighbor's, under the same sun, means a building roughly twice as tall."
    },
    {
      "type": "heading",
      "text": "What Time Was the Image Taken?"
    },
    {
      "type": "paragraph",
      "text": "Long shadows mean a low sun, which means early morning, late afternoon, the depths of winter, or a high latitude. Short, stubby shadows mean the sun is high — near local noon, summer, or closer to the tropics. Many imaging satellites are timed to pass over in the mid-morning, partly to keep shadows manageable and lighting consistent, so faint, modest shadows are the norm; dramatic long ones tell you something about the season or the latitude."
    },
    {
      "type": "heading",
      "text": "Terrain Reads Through Shadow Too"
    },
    {
      "type": "paragraph",
      "text": "Shadows are also what make mountains look like mountains from above. When low light rakes across a landscape, ridges catch the sun and valleys fall into shade, giving the relief a three-dimensional feel that a flat, overhead-noon image completely loses. Watch out for one quirk of perception: if the light seems to come from the bottom of the image, your brain can flip hills into pits and craters into domes. This 'relief inversion' fools almost everyone — rotate the view so the light falls from the top and the terrain snaps back to normal."
    },
    {
      "type": "heading",
      "text": "A Quick Field Test"
    },
    {
      "type": "paragraph",
      "text": "Put it together in three steps. First, find a tall, isolated object with a clean shadow — a lone tower, a chimney, a single big tree. Second, note which way the shadow points; the sun is in the exact opposite direction. Third, ask whether that fits a southern midday sun, which suggests the Northern Hemisphere, or a northern one, which suggests the Southern. Add the shadow's length for a rough sense of how high the sun sat, and in under ten seconds you have squeezed direction, hemisphere, and time of day out of a single patch of shade."
    },
    {
      "type": "heading",
      "text": "Don't Forget Clouds and Water"
    },
    {
      "type": "paragraph",
      "text": "Two bonus signals hide in the same trick. Clouds cast shadows onto the ground beside them, and the offset between a cloud and its shadow is another readout of sun angle. Water, meanwhile, can throw a bright 'sun glint' straight back at the sensor when the geometry lines up, turning lakes and seas silver — useful for telling calm water from land at a glance."
    }
  ]
};
